import { Box, Button, IconButton, keyframes, Stack, styled, Typography } from '@mui/material';
import { usePostFeedbackMutation } from '@schooly/api';
import { ControlTextField } from '@schooly/components/form-text-field';
import { useNotifications } from '@schooly/components/notifications';
import { VALID_EMAIL_REGEXP } from '@schooly/constants';
import { CloseIcon, SimpleButton, Spin } from '@schooly/style';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { IS_DEMO } from '../../config';
import { useSchool } from '../../hooks/useSchool';

enum BannerState {
  Question,
  Form,
  Success,
}

const BANNER_STORAGE_KEY = 'demo-school-cta-banner-shown';
const MAX_ROUTES_VISITED = 4;

export const DemoSchoolCtaBanner: FC = () => {
  const { $t } = useIntl();
  const { schoolId, currentSchool } = useSchool();
  const [routesVisited, setRoutesVisited] = useState<string[]>([]);
  const [bannerState, setBannerState] = useState<BannerState | null>(null);
  const { pathname } = useLocation();

  const sectionPath = pathname.split('/')[1];

  useEffect(() => {
    const key = `${BANNER_STORAGE_KEY}${schoolId}`;

    if (
      !schoolId ||
      !IS_DEMO ||
      !!bannerState ||
      !!localStorage.getItem(key) ||
      routesVisited.length >= MAX_ROUTES_VISITED ||
      routesVisited.includes(sectionPath)
    )
      return;

    const nextRoutesVisited = [...routesVisited, sectionPath];

    if (nextRoutesVisited.length === MAX_ROUTES_VISITED) {
      localStorage.setItem(key, 'true');
      setBannerState(BannerState.Question);
    }
    setRoutesVisited(nextRoutesVisited);
  }, [bannerState, routesVisited, schoolId, sectionPath]);

  const closeIcon = (
    <IconButton
      sx={(theme) => ({
        position: 'absolute',
        right: theme.spacing(2.5),
        top: theme.spacing(2.5),
      })}
      onClick={() => setBannerState(null)}
    >
      <CloseIcon />
    </IconButton>
  );

  if (!schoolId) return null;

  switch (bannerState) {
    case null:
      return null;
    case BannerState.Question:
      return (
        <BannerContainer>
          <Stack gap={2}>
            <Typography variant="h2">{$t({ id: 'school-demo-DoYouLikeWhat' })}</Typography>
            <SimpleButton onClick={() => setBannerState(BannerState.Form)}>
              {$t({ id: 'school-demo-NoIWantToExplore' })}
            </SimpleButton>
          </Stack>
        </BannerContainer>
      );
    case BannerState.Form:
      return (
        <BannerContainer>
          <Stack gap={2}>
            <Typography variant="h2">{$t({ id: 'school-demo-WereHappy' })}</Typography>

            <QuestionFormContent
              schoolName={currentSchool?.name}
              onSuccess={() => {
                setBannerState(BannerState.Success);
                setTimeout(() => setBannerState(null), 3000);
              }}
            />
          </Stack>
          {closeIcon}
        </BannerContainer>
      );
    case BannerState.Success:
      return (
        <BannerContainer>
          <Stack gap={2} alignItems="center">
            <SuccessSvg />
            <Typography variant="h2">{$t({ id: 'school-demo-WellGetBack' })}</Typography>
          </Stack>
          {closeIcon}
        </BannerContainer>
      );
  }
};

type QuestionForm = { email: string; question: string };

const QuestionFormContent: FC<{ onSuccess: () => void; schoolName?: string }> = ({
  onSuccess,
  schoolName,
}) => {
  const form = useForm<QuestionForm>();
  const { showError } = useNotifications();
  const sendFeedback = usePostFeedbackMutation();

  const { $t } = useIntl();

  const handleSubmit = useCallback<SubmitHandler<QuestionForm>>(
    async ({ question, email }) => {
      sendFeedback.mutate(
        { email, question, source: 'Demo school', school_name: schoolName },
        { onError: showError, onSuccess },
      );
    },
    [onSuccess, schoolName, sendFeedback, showError],
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap={1}>
          <Typography variant="h3">{$t({ id: 'school-demo-LetUsKnow' })}</Typography>

          <ControlTextField
            name="email"
            control={form.control}
            label={$t({ id: 'school-email' })}
            canClear
            fullWidth
            rules={{
              required: true,
              pattern: {
                value: VALID_EMAIL_REGEXP,
                message: $t({
                  id: 'input-ErrorInvalidEmail',
                }),
              },
            }}
          />
          <ControlTextField
            name="question"
            control={form.control}
            label={$t({ id: 'school-demo-HowCanWeHelp' })}
            canClear
            fullWidth
            multiline
            rows={4}
            sx={(theme) => ({
              '.MuiInputAdornment-root': {
                marginTop: `${theme.spacing(0.5)} !important`,
              },
            })}
          />
          <Button
            type="submit"
            disabled={sendFeedback.isLoading}
            startIcon={sendFeedback.isLoading ? <Spin /> : undefined}
          >
            {$t({ id: 'action-Submit' })}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
};

const BannerContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  animation: `${fadeIn} .5s ease-out`,
  zIndex: theme.zIndex.modal - 1,
  top: theme.spacing(2.5),
  right: theme.spacing(3),
  width: 400,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  boxShadow: '0px 10px 40px 0px rgba(37, 39, 90, 0.15)',
  padding: theme.spacing(2.5),
}));

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SuccessSvg: FC = () => (
  <svg
    width="250"
    height="250"
    viewBox="0 0 250 250"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M97.321 128.833H96.137V119.946H97.321V128.833ZM100.519 128.833H99.3779V119.458H100.519V128.833ZM103.559 128.833H102.417V119.458H103.559V128.833ZM109.4 122.229H110.541V128.833H109.455L109.4 127.307V122.229ZM110.09 125.903C110.09 126.489 110.006 127.012 109.839 127.472C109.673 127.932 109.4 128.294 109.021 128.558C108.643 128.823 108.136 128.955 107.502 128.955C106.855 128.955 106.332 128.764 105.933 128.381C105.534 127.999 105.335 127.368 105.335 126.489V122.229H106.476V126.501C106.476 126.9 106.533 127.209 106.647 127.429C106.765 127.645 106.916 127.795 107.099 127.881C107.286 127.962 107.481 128.003 107.685 128.003C108.165 128.003 108.545 127.911 108.826 127.728C109.107 127.541 109.308 127.289 109.43 126.971C109.552 126.654 109.614 126.302 109.614 125.916L110.09 125.903ZM116.102 127.081C116.102 126.919 116.065 126.768 115.992 126.63C115.923 126.487 115.78 126.359 115.564 126.245C115.349 126.127 115.023 126.023 114.588 125.934C114.1 125.832 113.676 125.704 113.318 125.549C112.964 125.391 112.69 125.189 112.494 124.945C112.303 124.701 112.208 124.398 112.208 124.036C112.208 123.686 112.307 123.366 112.507 123.077C112.71 122.784 112.995 122.55 113.361 122.375C113.731 122.196 114.163 122.107 114.655 122.107C115.184 122.107 115.636 122.201 116.01 122.388C116.388 122.571 116.677 122.819 116.877 123.132C117.076 123.442 117.176 123.785 117.176 124.164H116.034C116.034 123.891 115.914 123.633 115.674 123.389C115.438 123.145 115.099 123.022 114.655 123.022C114.346 123.022 114.094 123.071 113.898 123.169C113.707 123.263 113.567 123.385 113.477 123.535C113.392 123.682 113.349 123.836 113.349 123.999C113.349 124.162 113.39 124.304 113.471 124.426C113.552 124.544 113.701 124.65 113.917 124.744C114.136 124.837 114.454 124.931 114.869 125.024C115.41 125.146 115.855 125.293 116.205 125.464C116.555 125.635 116.816 125.844 116.987 126.093C117.157 126.341 117.243 126.644 117.243 127.002C117.243 127.384 117.137 127.724 116.926 128.021C116.714 128.314 116.417 128.544 116.034 128.711C115.652 128.874 115.202 128.955 114.686 128.955C114.104 128.955 113.611 128.851 113.208 128.644C112.806 128.436 112.5 128.17 112.293 127.844C112.085 127.515 111.982 127.167 111.982 126.801H113.123C113.143 127.11 113.233 127.356 113.392 127.539C113.554 127.718 113.752 127.846 113.984 127.924C114.22 128.001 114.454 128.04 114.686 128.04C114.995 128.04 115.253 127.999 115.461 127.917C115.672 127.832 115.831 127.718 115.937 127.576C116.047 127.429 116.102 127.264 116.102 127.081ZM121.491 123.09H117.914V122.229H121.491V123.09ZM120.258 120.624V127.197C120.258 127.425 120.293 127.596 120.362 127.71C120.431 127.824 120.521 127.899 120.63 127.936C120.74 127.972 120.858 127.991 120.984 127.991C121.078 127.991 121.176 127.983 121.277 127.966C121.383 127.95 121.46 127.936 121.509 127.924L121.515 128.833C121.426 128.861 121.308 128.888 121.161 128.912C121.019 128.941 120.846 128.955 120.643 128.955C120.366 128.955 120.112 128.9 119.88 128.79C119.648 128.68 119.463 128.497 119.324 128.241C119.186 127.981 119.117 127.631 119.117 127.191V120.624H120.258ZM123.945 128.833H122.803V122.229H123.914L123.945 123.267V128.833ZM126.001 123.236C125.904 123.216 125.812 123.204 125.727 123.199C125.645 123.191 125.552 123.187 125.446 123.187C125.1 123.187 124.807 123.258 124.567 123.401C124.331 123.543 124.148 123.741 124.018 123.993C123.888 124.241 123.806 124.526 123.774 124.847L123.45 125.031C123.45 124.497 123.521 124.011 123.664 123.572C123.806 123.128 124.028 122.774 124.329 122.51C124.634 122.241 125.025 122.107 125.501 122.107C125.578 122.107 125.67 122.117 125.776 122.137C125.885 122.154 125.961 122.172 126.001 122.192V123.236ZM130.738 124.304C130.738 123.91 130.62 123.596 130.384 123.364C130.152 123.128 129.808 123.01 129.352 123.01C129.072 123.01 128.823 123.059 128.608 123.157C128.396 123.254 128.231 123.383 128.113 123.541C127.995 123.7 127.936 123.873 127.936 124.06H126.795C126.795 123.743 126.903 123.435 127.118 123.138C127.334 122.837 127.637 122.591 128.028 122.4C128.423 122.205 128.886 122.107 129.419 122.107C129.9 122.107 130.325 122.188 130.695 122.351C131.065 122.514 131.354 122.76 131.562 123.09C131.773 123.415 131.879 123.824 131.879 124.316V127.393C131.879 127.612 131.897 127.846 131.934 128.094C131.975 128.343 132.034 128.556 132.111 128.735V128.833H130.921C130.864 128.703 130.819 128.53 130.787 128.314C130.754 128.094 130.738 127.891 130.738 127.704V124.304ZM130.945 125.623H129.81C129.167 125.623 128.671 125.724 128.321 125.928C127.971 126.131 127.796 126.436 127.796 126.843C127.796 127.161 127.902 127.429 128.113 127.649C128.329 127.869 128.642 127.979 129.053 127.979C129.399 127.979 129.704 127.905 129.969 127.759C130.233 127.612 130.443 127.431 130.597 127.216C130.752 127 130.835 126.792 130.848 126.593L131.336 127.136C131.307 127.307 131.23 127.496 131.104 127.704C130.978 127.911 130.809 128.111 130.597 128.302C130.39 128.489 130.142 128.646 129.853 128.772C129.568 128.894 129.247 128.955 128.888 128.955C128.441 128.955 128.048 128.87 127.71 128.699C127.377 128.524 127.116 128.288 126.929 127.991C126.746 127.694 126.655 127.364 126.655 127.002C126.655 126.29 126.923 125.751 127.46 125.385C127.997 125.014 128.72 124.829 129.627 124.829H130.933L130.945 125.623ZM136.432 123.09H132.856V122.229H136.432V123.09ZM135.199 120.624V127.197C135.199 127.425 135.234 127.596 135.303 127.71C135.372 127.824 135.462 127.899 135.572 127.936C135.682 127.972 135.8 127.991 135.926 127.991C136.019 127.991 136.117 127.983 136.219 127.966C136.325 127.95 136.402 127.936 136.451 127.924L136.457 128.833C136.367 128.861 136.249 128.888 136.103 128.912C135.96 128.941 135.787 128.955 135.584 128.955C135.307 128.955 135.053 128.9 134.821 128.79C134.589 128.68 134.404 128.497 134.266 128.241C134.127 127.981 134.058 127.631 134.058 127.191V120.624H135.199ZM138.984 128.833H137.842V122.229H138.984V128.833ZM137.757 120.471C137.757 120.292 137.812 120.14 137.922 120.013C138.036 119.887 138.202 119.824 138.422 119.824C138.642 119.824 138.809 119.887 138.923 120.013C139.041 120.14 139.1 120.292 139.1 120.471C139.1 120.646 139.041 120.797 138.923 120.923C138.809 121.045 138.642 121.106 138.422 121.106C138.202 121.106 138.036 121.045 137.922 120.923C137.812 120.797 137.757 120.646 137.757 120.471ZM140.491 125.464C140.491 124.825 140.613 124.253 140.857 123.749C141.102 123.244 141.447 122.845 141.895 122.552C142.343 122.255 142.874 122.107 143.488 122.107C144.111 122.107 144.648 122.255 145.099 122.552C145.551 122.845 145.897 123.244 146.137 123.749C146.381 124.253 146.503 124.825 146.503 125.464V125.604C146.503 126.239 146.381 126.811 146.137 127.319C145.897 127.824 145.551 128.223 145.099 128.516C144.652 128.809 144.119 128.955 143.5 128.955C142.882 128.955 142.347 128.809 141.895 128.516C141.447 128.223 141.102 127.824 140.857 127.319C140.613 126.811 140.491 126.239 140.491 125.604V125.464ZM141.633 125.604C141.633 126.04 141.702 126.442 141.84 126.813C141.978 127.183 142.184 127.48 142.457 127.704C142.733 127.928 143.081 128.04 143.5 128.04C143.915 128.04 144.259 127.928 144.532 127.704C144.808 127.48 145.016 127.183 145.154 126.813C145.293 126.442 145.362 126.04 145.362 125.604V125.464C145.362 125.028 145.293 124.626 145.154 124.255C145.016 123.885 144.808 123.588 144.532 123.364C144.255 123.136 143.907 123.022 143.488 123.022C143.077 123.022 142.733 123.136 142.457 123.364C142.18 123.588 141.972 123.885 141.834 124.255C141.7 124.626 141.633 125.028 141.633 125.464V125.604ZM149.067 128.833H147.925V122.229H149.006L149.067 123.639V128.833ZM148.322 125.256C148.33 124.654 148.446 124.117 148.67 123.645C148.894 123.169 149.205 122.795 149.604 122.522C150.007 122.245 150.475 122.107 151.008 122.107C151.443 122.107 151.817 122.188 152.131 122.351C152.448 122.51 152.692 122.764 152.863 123.114C153.038 123.464 153.125 123.928 153.125 124.506V128.833H151.984V124.493C151.984 124.148 151.933 123.869 151.832 123.657C151.734 123.446 151.587 123.293 151.392 123.199C151.197 123.102 150.957 123.053 150.672 123.053C150.391 123.053 150.137 123.112 149.909 123.23C149.681 123.348 149.484 123.511 149.317 123.718C149.15 123.926 149.022 124.164 148.932 124.432C148.843 124.697 148.798 124.978 148.798 125.275L148.322 125.256Z"
      fill="#B3BBCB"
    />
    <path
      d="M193.333 51.6667L216.666 45"
      stroke="#24275B"
      strokeWidth="0.833333"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M189.167 44.167L198.334 14.167"
      stroke="#24275B"
      strokeWidth="0.833333"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M55.6918 189.15L24.1973 197.65"
      stroke="#24275B"
      strokeWidth="0.833333"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M60.8364 200.573L46.3828 239.941"
      stroke="#24275B"
      strokeWidth="0.833333"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M162.62 145.867C181.872 112.302 179.256 74.5725 156.776 61.595C134.296 48.6176 100.465 65.3068 81.2119 98.8714C61.959 132.436 64.5753 170.166 87.0554 183.143C109.536 196.121 143.367 179.432 162.62 145.867Z"
      fill="#24275B"
    />
    <path d="M102.083 189.583L90.833 185L104.166 185.417L102.083 189.583Z" fill="#24275B" />
    <path d="M152.5 59.5837L163.75 64.167L146.586 63.2308L152.5 59.5837Z" fill="#24275B" />
    <path
      d="M172.44 149.878C191.693 116.313 189.076 78.5832 166.596 65.6058C144.116 52.6283 110.285 69.3175 91.0322 102.882C71.7794 136.447 74.3956 174.177 96.8757 187.154C119.356 200.131 153.187 183.442 172.44 149.878Z"
      fill="#E7E9F4"
    />
    <path
      d="M65.833 103.333V105.833L109.166 171.25L184.583 72.083V69.583L175.833 59.1663L174.166 58.333L172.083 58.7497L110 139.583L78.333 90.833H76.6663L65.833 103.333Z"
      fill="white"
    />
    <path
      d="M78.333 108.128L109.597 155.833L177.5 65.833"
      stroke="white"
      strokeWidth="18.3333"
      strokeLinecap="round"
    />
    <path
      d="M183.333 74.167L109.166 171.667L65.9835 106.691C65.1556 105.445 65.2581 103.801 66.2351 102.669C68.2135 100.375 71.6777 96.358 74.9997 92.5003"
      stroke="#24275B"
      strokeWidth="0.833333"
      strokeLinecap="round"
    />
    <path
      d="M169.166 62.083L110 139.583L79.3147 92.3754C78.4069 90.9787 76.4128 90.8438 75.3251 92.1055L67.083 101.666"
      stroke="#24275B"
      strokeWidth="0.833333"
      strokeLinecap="round"
    />
    <g clip-path="url(#clip0_8476_208922)">
      <path
        d="M152.082 49.8743C108.641 24.498 60.9963 24.2093 38.3193 51.7863C16.6365 78.1543 24.5092 121.815 54.5345 158.333M207.499 103.016C227.941 135.768 231.189 171.304 212.515 194.014C190.713 220.527 145.831 221.283 103.795 198.75"
        stroke="#24275B"
        strokeWidth="0.833333"
        strokeMiterlimit="10"
        strokeLinejoin="round"
      />
    </g>
    <path
      d="M165.417 67.0833L173.203 56.7016C174.264 55.2873 176.417 55.3907 177.338 56.9001L184.652 68.8959C185.365 70.065 185.291 71.551 184.464 72.6428L174.167 86.25"
      stroke="#24275B"
      strokeWidth="0.833333"
      strokeLinecap="round"
    />
    <defs>
      <clipPath id="clip0_8476_208922">
        <rect width="199.167" height="184.167" fill="white" transform="translate(25.833 30.833)" />
      </clipPath>
    </defs>
  </svg>
);
